import { PersonalDetails } from "./Steps/PersonalDetails";
import { Financing } from "./Steps/Financing";
import { TradeIn } from "./Steps/TradeIn";
import { BuyRequest } from "./Steps/BuyRequest";
import { Dropdown } from "../../Components/Form/Dropdown";
import { Checkbox } from "../../Components/Form/Checkbox";
import { ShowThis } from "../ShowThis";
import { DialogListener } from "../../Components/Dialog/DialogListener";
import { AbstractSidebar } from "../Sidebar/Abstract";
import { Toast } from "../../Components/Dialog/Toast";
import { EVENTS } from "./Constants";
import { InputText } from "../../Components/Form/Input";
import { formatThousandSeparator } from "../../Utils/Number";
import { HTTPError } from "../../Exceptions/HTTPError";

export class VehicleRequest {
    static EVENTS = EVENTS;

    static SUBMIT_URL = "/apps/rental-appointment/api/submit-motorcycle-request";

    private readonly formWrapper: HTMLFormElement;

    constructor(formWrapper: HTMLFormElement) {
        this.formWrapper = formWrapper;
    }

    private financingDropdownListener: () => void;
    private tradeInDropdownListener: () => void;

    init(): void {
        this.addListeners();
    }

    /**
     * add event listeners
     */
    addListeners() {
        document.querySelectorAll(".request-vehicle > .btn")?.forEach((btn) => {
            btn.addEventListener("click", this.handleStart.bind(this));
        });
        this.formWrapper.addEventListener("submit", this.handleFormSubmit.bind(this));
        window.addEventListener(VehicleRequest.EVENTS.INITIAL_REQUEST, this.handleInitialRequest.bind(this));
        window.addEventListener(VehicleRequest.EVENTS.FINANCING, this.handleContinueFinancing.bind(this));
        window.addEventListener(VehicleRequest.EVENTS.TRADE_IN, this.handleContinueTradeIn.bind(this));
        this.financingDropdownListener = this.handleDropdownChange.bind(this);
        this.tradeInDropdownListener = this.handleDropdownChange.bind(this);
    }

    private handleDropdownChange(event: Event): void {
        const target = event.currentTarget as Dropdown;
        const closestSidebarItem = target.closest(".sidebar-item");
        const dropdownForm = closestSidebarItem.classList.contains("configure-financing")
            ? "configure-financing"
            : "configure-trade-in";
        const value = target.value === "Yes";
        if (value) {
            const formSelector = `#${dropdownForm}-form`;
            const form = document.querySelector<HTMLTemplateElement>(formSelector).content.cloneNode(true);
            const element = document.querySelector(`.${dropdownForm} > div`);
            if (element.childElementCount < 1) {
                element.appendChild(form);
                const showVehicleIDCheckbox = this.formWrapper.querySelector<Checkbox>(ShowThis.INITIATORS.showThis);
                if (showVehicleIDCheckbox) new ShowThis(showVehicleIDCheckbox);
                const modalInitiators = this.formWrapper.querySelectorAll<HTMLElement>(
                    DialogListener.INITIATORS.reveal
                );
                modalInitiators.forEach((initiator) => new DialogListener(initiator));
                const decimalInputs = this.formWrapper.querySelectorAll<InputText>('[inputmode="decimal"]');
                decimalInputs.forEach((input) =>
                    input.addEventListener("input", () => this.handleThousandSeparator(input))
                );
            }
        } else {
            document.querySelector(`.${dropdownForm}-form`)?.remove();
        }
    }

    private async handleFormSubmit(event: Event): Promise<void> {
        event.preventDefault();
        if (!this.formWrapper.checkValidity()) return;
        if (this.formWrapper.childElementCount < 4) return; // Not all form fields are included then
        this.formWrapper.querySelectorAll<HTMLElement>('.btn[type="submit"]').forEach((btn) => {
            btn.setAttribute("disabled", "disabled");
        });
        this.formWrapper.removeEventListener("submit", this.handleFormSubmit);
        const response = await fetch(VehicleRequest.SUBMIT_URL, {
            method: "POST",
            body: new FormData(this.formWrapper)
        });
        if (response.status !== 200) {
            window.dispatchEvent(new CustomEvent("show-generic-error-message"));
            throw new HTTPError(response.status, response.statusText);
        } else {
            new Toast({
                title: window.SuccessMessages.purchaseRequest.success,
                type: Toast.TYPE_SUCCESS
            })?.render();
        }
        window.dispatchEvent(new CustomEvent(AbstractSidebar.DESTROY_ALL_EVENT));
    }

    handleInitialRequest(): void {
        window.addEventListener(
            AbstractSidebar.RENDER_DONE_EVENT,
            () => {
                const dropdown = document.querySelector(".configure-financing > x-dropdown") as Dropdown;
                dropdown.addEventListener("change", this.financingDropdownListener);
            },
            { once: true }
        );
        new Financing(this.formWrapper).render();
    }

    handleStart(): void {
        new BuyRequest(this.formWrapper).render();
    }

    handleThousandSeparator(input: InputText): void {
        formatThousandSeparator(input);
    }

    handleContinueFinancing(): void {
        window.addEventListener(
            AbstractSidebar.RENDER_DONE_EVENT,
            () => {
                const dropdown = document.querySelector(".configure-trade-in > x-dropdown") as Dropdown;
                dropdown.addEventListener("change", this.financingDropdownListener);
            },
            { once: true }
        );
        new TradeIn(this.formWrapper).render();
    }

    handleContinueTradeIn(): void {
        new PersonalDetails(this.formWrapper).render();
    }
}

if (document.querySelector(".request-vehicle")) {
    const form = document.createElement("form");
    form.classList.add("std-form");
    form.setAttribute("novalidate", "novalidate");
    form.style.zIndex = "3"; // should render above sticky-cta
    document.body.appendChild(form);
    new VehicleRequest(form).init();
}
